/**
 * Datos gestionados por el sistema externo (009 · US6 · T054–T055; contracts/ui.md §7, contrato §6).
 *
 * Una sede o trabajador vinculado trae `managed_by { system_name, fields }`: esos campos son de
 * solo lectura en Fermin (la API responde `409 INTEGRATION_MANAGED` si se intenta cambiarlos) y se
 * muestran bloqueados con la leyenda "Gestionado por <sistema>". Los campos que el origen no envía
 * (teléfono, zona, radio…) siguen editables.
 *
 * - `isManaged(entity, field)` / `managedFieldsOf(entity)`.
 * - `ManagedFieldHint`: candado + leyenda, enlazado al campo con `aria-describedby={id}`
 *   (`describedByWith` suma su id al del `Field`).
 * - `ManagedChip`: chip "Integrada" para listados y cabeceras (el título dice el sistema).
 * - `ManagedSourceLabel`: origen "Gestionado por <sistema>" de horarios, feriados y ausencias
 *   integrados (`source = INTEGRATION`), que no traen `managed_by`: el nombre sale de `GET /me`.
 * - `omitManaged(body, entity)`: quita del `PATCH` los campos gestionados (la API igual revalida).
 */
import { Lock } from 'lucide-react';

import type { ManagedBy } from '@fermin/contracts';

import { formatDate, t } from '../../i18n';
import { useMe } from '../shell/use-me';

type ManagedEntity = { managed_by?: ManagedBy | null | undefined } | null | undefined;

/** `true` si el campo de la sede/trabajador lo gestiona el sistema externo. */
export function isManaged(entity: ManagedEntity, field: string): boolean {
  return entity?.managed_by?.fields.includes(field) ?? false;
}

/** Campos gestionados de la entidad (vacío si no está vinculada). */
export function managedFieldsOf(entity: ManagedEntity): ReadonlySet<string> {
  return new Set(entity?.managed_by?.fields ?? []);
}

/**
 * Copia de `body` sin los campos que gestiona el sistema externo: el formulario los muestra
 * bloqueados y no los reenvía (un valor distinto respondería `409 INTEGRATION_MANAGED`).
 */
export function omitManaged<T extends object>(body: T, entity: ManagedEntity): Partial<T> {
  const managed = managedFieldsOf(entity);
  if (managed.size === 0) {
    return body;
  }
  return Object.fromEntries(
    Object.entries(body).filter(([key]) => !managed.has(key)),
  ) as Partial<T>;
}

/** Suma el id de la leyenda al `aria-describedby` que arma `Field`. */
export function describedByWith(
  describedBy: string | undefined,
  hintId: string | undefined,
): string | undefined {
  return [describedBy, hintId].filter(Boolean).join(' ') || undefined;
}

/** Nombre del sistema externo de la empresa (`GET /me`), o `null` sin conexión. */
export function useIntegrationSystemName(): string | null {
  const me = useMe();
  return me.data?.integration?.system_name ?? null;
}

/** "Gestionado por <sistema>" (o "Integración" si todavía no se conoce el nombre). */
export function managedLabel(systemName: string | null | undefined): string {
  return systemName
    ? t('integration.managed.hint', { system: systemName })
    : t('schedules.source.INTEGRATION');
}

/** `2026-12-31` → `31/12/2026` (fecha local, sin zona). */
export function localDateLabel(day: string): string {
  return formatDate(`${day}T12:00:00.000Z`, 'UTC');
}

export interface ManagedFieldHintProps {
  systemName: string;
  /** Id del texto, para `aria-describedby` del campo bloqueado. */
  id?: string;
}

export function ManagedFieldHint({ systemName, id }: ManagedFieldHintProps) {
  return (
    <p
      id={id}
      data-testid="managed-field-hint"
      className="flex items-center gap-fm-1 text-fm-xs text-fm-text-secondary"
    >
      <Lock size={12} aria-hidden="true" />
      {t('integration.managed.hint', { system: systemName })}
    </p>
  );
}

/** Chip "Integrada" (listados y cabeceras); el nombre del sistema va en el título y para lectores. */
export function ManagedChip({ systemName }: { systemName: string | null }) {
  const label = managedLabel(systemName);
  return (
    <span
      data-testid="managed-chip"
      title={label}
      className="inline-flex items-center gap-fm-1 rounded-fm-sm bg-fm-bg-sunken px-fm-2 py-px text-fm-xs font-semibold text-fm-text"
    >
      <Lock size={12} aria-hidden="true" />
      <span aria-hidden="true">{t('integration.managed.chip')}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

/**
 * Origen de un horario, feriado o ausencia: "Manual", o candado + "Gestionado por <sistema>" si es
 * integrado (008: la API ya los bloquea; aquí solo se explica por qué no hay acciones).
 */
export function ManagedSourceLabel({
  source,
  systemName,
}: {
  source: 'MANUAL' | 'INTEGRATION';
  systemName: string | null;
}) {
  if (source !== 'INTEGRATION') {
    return <>{t('schedules.source.MANUAL')}</>;
  }
  return (
    <span className="inline-flex items-center gap-fm-1" data-testid="managed-source">
      <Lock size={12} aria-hidden="true" />
      {managedLabel(systemName)}
    </span>
  );
}
